"use client";

import type { FormLabels } from "../types";

interface Data {
  street: string;
  house_number: string;
  postal_code: string;
  city: string;
}

interface Props {
  labels: FormLabels;
  data: Data;
  onChange: (d: Data) => void;
  onSubmit: (e: React.FormEvent) => void;
  onSkipDigital: () => void;
  submitting: boolean;
}

const INPUT_CLS =
  "h-[52px] w-full rounded-card border-2 border-stone-100 bg-white px-4 text-base text-ink placeholder:text-stone-300 transition-colors focus:border-terracotta focus:outline-none";

function isValidPostcode(v: string): boolean {
  return /^\d{4}\s?[A-Z]{2}$/i.test(v.trim());
}

export default function Step2Address({
  labels,
  data,
  onChange,
  onSubmit,
  onSkipDigital,
  submitting,
}: Props) {
  const valid =
    data.street.trim().length > 0 &&
    data.house_number.trim().length > 0 &&
    isValidPostcode(data.postal_code) &&
    data.city.trim().length > 0;

  return (
    <form onSubmit={onSubmit} className="space-y-4" noValidate>
      <h2 className="font-display text-2xl md:text-3xl text-ink leading-tight mb-3">
        {labels.step_2_title}
      </h2>

      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="block">
            <span className="text-sm font-medium text-ink block mb-1.5">
              {labels.street_label}
            </span>
            <input
              type="text"
              value={data.street}
              onChange={(e) => onChange({ ...data, street: e.target.value })}
              required
              autoComplete="address-line1"
              className={INPUT_CLS}
            />
          </label>
        </div>
        <label className="block">
          <span className="text-sm font-medium text-ink block mb-1.5">
            {labels.house_number_label}
          </span>
          <input
            type="text"
            value={data.house_number}
            onChange={(e) => onChange({ ...data, house_number: e.target.value })}
            required
            autoComplete="address-line2"
            inputMode="numeric"
            className={INPUT_CLS}
          />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <span className="text-sm font-medium text-ink block mb-1.5">
            {labels.postal_code_label}
          </span>
          <input
            type="text"
            value={data.postal_code}
            onChange={(e) => onChange({ ...data, postal_code: e.target.value.toUpperCase() })}
            required
            autoComplete="postal-code"
            placeholder="1234 AB"
            className={INPUT_CLS}
          />
        </label>
        <label className="block">
          <span className="text-sm font-medium text-ink block mb-1.5">
            {labels.city_label}
          </span>
          <input
            type="text"
            value={data.city}
            onChange={(e) => onChange({ ...data, city: e.target.value })}
            required
            autoComplete="address-level2"
            className={INPUT_CLS}
          />
        </label>
      </div>

      <button
        type="submit"
        disabled={!valid || submitting}
        className="w-full rounded-pill bg-terracotta text-white font-semibold py-4 text-base shadow-card hover:bg-terracotta-700 hover:shadow-card-hover disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-terracotta disabled:hover:shadow-card transition-all mt-2 focus:outline-none focus-visible:shadow-focus"
      >
        {submitting ? "Verzenden…" : labels.submit_step_2}
      </button>

      <button
        type="button"
        onClick={onSkipDigital}
        className="block mx-auto text-sm text-stone-700 hover:text-ink underline underline-offset-2 mt-2 focus:outline-none focus-visible:shadow-focus rounded"
      >
        {labels.digital_only_link}
      </button>
    </form>
  );
}
